import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EmpresaTenant, FechamentoEvento, FechamentoEventoStatus, FechamentoEventoTipo } from './models';
import workflowService from './workflowService';
import { chaveDia, eventosDeFeriados, eventosDePrazos } from './calendarioEventos';

const statusLabel: Record<FechamentoEventoStatus, string> = {
  CONCLUIDO: 'Concluído',
  EM_ANDAMENTO: 'Em andamento',
  PENDENTE: 'Pendente',
  ATRASADO: 'Atrasado',
};

const tipoLabel: Record<FechamentoEventoTipo, string> = {
  ABERTURA: 'Abertura',
  PRAZO_NIVEL: 'Prazo de nível',
  AUDITORIA: 'Auditoria',
  ENCERRAMENTO: 'Encerramento',
  FERIADO: 'Feriado nacional',
};

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const toKey = chaveDia;

const EventStatusBadge: React.FC<{ evento: FechamentoEvento }> = ({ evento }) => evento.tipo === 'FERIADO'
  ? <span className='wf-cal-badge wf-cal-badge--feriado'>Feriado</span>
  : <span className={`wf-cal-badge wf-cal-badge--${evento.status.toLowerCase()}`}>{statusLabel[evento.status]}</span>;

const dotClasse = (e: FechamentoEvento) => (e.tipo === 'FERIADO' ? 'feriado' : e.status.toLowerCase());

const CalendarioFechamento: React.FC = () => {
  const history = useHistory();
  const hoje = toKey(new Date());
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState<string | null>(hoje);
  const [prazos, setPrazos] = useState<FechamentoEvento[]>([]);
  const [feriadosPorAno, setFeriadosPorAno] = useState<Record<number, FechamentoEvento[]>>({});
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [empresaId, setEmpresaId] = useState('');
  const [erroPrazos, setErroPrazos] = useState(false);
  const ano = cursor.getFullYear();

  useEffect(() => {
    workflowService.listarTenants()
      .then(lista => setEmpresas(lista.filter(e => e.status === 'ATIVA')))
      .catch(() => setEmpresas([]));
  }, []);

  // Prazos de SLA: todas as empresas ou só a escolhida no filtro.
  useEffect(() => {
    setErroPrazos(false);
    workflowService.listarMonitoramento(empresaId ? Number(empresaId) : undefined)
      .then(m => setPrazos(eventosDePrazos(m?.itens ?? [])))
      .catch(() => { setPrazos([]); setErroPrazos(true); });
  }, [empresaId]);

  // Feriados nacionais do ano exibido (cada ano é buscado uma vez).
  useEffect(() => {
    if (feriadosPorAno[ano]) return;
    workflowService.listarFeriados(ano)
      .then(r => setFeriadosPorAno(prev => ({ ...prev, [ano]: eventosDeFeriados(r.feriados ?? []) })))
      .catch(() => setFeriadosPorAno(prev => ({ ...prev, [ano]: [] })));
  }, [ano, feriadosPorAno]);

  const eventos = useMemo(
    () => [...prazos, ...Object.values(feriadosPorAno).reduce<FechamentoEvento[]>((acc, l) => acc.concat(l), [])],
    [prazos, feriadosPorAno]);
  const semFeriados = feriadosPorAno[ano] !== undefined && feriadosPorAno[ano].length === 0;

  const eventsByDay = useMemo(() => {
    const map = new Map<string, FechamentoEvento[]>();
    eventos.forEach(evento => {
      const list = map.get(evento.data) || [];
      list.push(evento);
      map.set(evento.data, list);
    });
    return map;
  }, [eventos]);

  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: { date: Date; inMonth: boolean }[] = [];
    for (let i = 0; i < startOffset; i++) cells.push({ date: new Date(year, month, i - startOffset + 1), inMonth: false });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), inMonth: true });
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      cells.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), inMonth: false });
    }
    return cells;
  }, [cursor]);

  const selectedEvents = selected ? (eventsByDay.get(selected) || []) : [];
  const upcoming = [...prazos].sort((a, b) => a.data.localeCompare(b.data));

  return <div className='wf-page'>
    <div className='wf-header'>
      <div><h1>Calendário de Fechamento</h1><p>Datas de abertura, prazos por nível, auditoria e encerramento do ciclo contábil.</p></div>
      <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>
    </div>

    <section className='wf-card' style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#5e6278', display: 'flex', alignItems: 'center', gap: 8 }}>
        Empresa
        <select value={empresaId} onChange={e => setEmpresaId(e.target.value)} aria-label='Empresa do calendário'
          style={{ minHeight: 34, padding: '4px 8px', border: '1px solid #dfe3ea', borderRadius: 7, fontSize: 13, minWidth: 260 }}>
          <option value=''>Todas as empresas</option>
          {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
      </label>
      {erroPrazos && <p className='wf-danger-text' style={{ margin: '8px 0 0', fontSize: 12 }}>Não foi possível carregar os prazos desta empresa. Tente novamente.</p>}
    </section>

    <div className='wf-cal-layout'>
      <section className='wf-card wf-cal-card'>
        <div className='wf-cal-toolbar'>
          <button className='wf-btn wf-btn--secondary' onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹ Mês anterior</button>
          <strong>{monthNames[cursor.getMonth()]} de {cursor.getFullYear()}</strong>
          <button className='wf-btn wf-btn--secondary' onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>Mês seguinte ›</button>
        </div>
        <div className='wf-cal-grid wf-cal-grid--head'>{weekDays.map(d => <div key={d}>{d}</div>)}</div>
        <div className='wf-cal-grid'>
          {grid.map(({ date, inMonth }, index) => {
            const key = toKey(date);
            const events = eventsByDay.get(key) || [];
            const isToday = key === hoje;
            const isSelected = key === selected;
            return <button
              key={index}
              className={`wf-cal-day ${inMonth ? '' : 'is-muted'} ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}`}
              onClick={() => setSelected(key)}
            >
              <span>{date.getDate()}</span>
              {events.length > 0 && <div className='wf-cal-dots'>{events.map(e => <i key={e.id} className={`wf-cal-dot wf-cal-dot--${dotClasse(e)}`} title={e.titulo} />)}</div>}
            </button>;
          })}
        </div>
        <div className='wf-cal-legend'>
          <span><i className='wf-cal-dot wf-cal-dot--concluido' />Concluído</span>
          <span><i className='wf-cal-dot wf-cal-dot--em_andamento' />Em andamento</span>
          <span><i className='wf-cal-dot wf-cal-dot--pendente' />Pendente</span>
          <span><i className='wf-cal-dot wf-cal-dot--atrasado' />Atrasado</span>
          <span><i className='wf-cal-dot wf-cal-dot--feriado' />Feriado nacional</span>
        </div>
      </section>

      <aside>
        <section className='wf-card'>
          <div className='wf-card__header'><div><h2>{selected ? `Eventos em ${selected.split('-').reverse().join('/')}` : 'Selecione um dia'}</h2></div></div>
          {selectedEvents.length === 0 && <p className='wf-count'>Nenhum prazo ou feriado nesta data.</p>}
          {semFeriados && <p className='wf-count'>Feriados de {ano} ainda não sincronizados — sincronize o calendário fiscal no Onboarding (Etapa 4).</p>}
          <div className='wf-cal-events'>
            {selectedEvents.map(evento => <div className='wf-cal-event' key={evento.id}>
              <div className='wf-cal-event__top'><strong>{evento.titulo}</strong><EventStatusBadge evento={evento} /></div>
              <span className='wf-cal-event__meta'>{tipoLabel[evento.tipo]}{evento.nivel ? ` • Nível ${evento.nivel}` : ''} • {evento.empresa}</span>
              <p>{evento.descricao}</p>
            </div>)}
          </div>
        </section>

        <section className='wf-card'>
          <div className='wf-card__header'><div><h2>Próximos prazos do ciclo</h2><p>Conciliações com prazo de SLA, em ordem cronológica.</p></div></div>
          {upcoming.length === 0 && <p className='wf-count'>Nenhuma conciliação com prazo configurado.</p>}
          <div className='wf-cal-events'>
            {upcoming.map(evento => <div className={`wf-cal-event wf-cal-event--compact ${evento.data === selected ? 'is-active' : ''}`} key={evento.id} onClick={() => setSelected(evento.data)}>
              <div className='wf-cal-event__top'><strong>{evento.data.split('-').reverse().join('/')} — {evento.titulo}</strong><EventStatusBadge evento={evento} /></div>
              <span className='wf-cal-event__meta'>{tipoLabel[evento.tipo]}{evento.nivel ? ` • Nível ${evento.nivel}` : ''} • {evento.empresa}</span>
            </div>)}
          </div>
        </section>
      </aside>
    </div>
  </div>;
};

export default CalendarioFechamento;
